import React, { useState, useEffect, useRef, useCallback } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { Play, Sun, Moon, Trash2, FileText } from "lucide-react";
import { dbEngine, updateCodeSandboxProgress, getSignedUrl } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { useParams } from "react-router-dom";
import { Col, Row, Container, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { oneDark, defaultLightThemeOption } from "@uiw/react-codemirror";
import { formatSQLTable } from "./formatSQL";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ROLES } from "constants/Constants";
import { createAssistanceExtensions } from "./codeAssistance";
import ChatComponent from './ChatComponent';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleCodeReview } from "../../../../dashboard/user-landing-page/codeReviewUtils";
import useProctoring from "../proctoring/useProctoring";
import ProctoredCodeEditorDB from "./ProctoredCodeEditorDB";

const CodeEditorDB = ({ problemStatement, activeContent }) => {
  const [cookies] = useCookies(["roleId", "userId"]);
  const { courseId, sectionId } = useParams();

  const hasBeenSubmittedBefore = Boolean(activeContent?.submitReason) || Boolean(activeContent?.completed && activeContent?.codeSnippet);
  const isProctored = activeContent?.isProctored === true;

  const [userId, setUserId] = useState(cookies.userId || null);
  const [files, setFiles] = useState([
    {
      id: 1,
      name: "main.sql",
      language: "sql",
      content: (hasBeenSubmittedBefore && activeContent?.codeSnippet)
        ? activeContent.codeSnippet
        : "-- Write your SQL query here",
    }
  ]);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [tables, setTables] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isAssistanceEnabled, setIsAssistanceEnabled] = useState(true);

  const sessionRef = useRef(null);
  const proctoringContainerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const lastSnapshotCodeRef = useRef(null);

  // ── Proctoring state ────────────────────────────────────────────────────────
  const [showProctoringStart, setShowProctoringStart] = useState(isProctored && !hasBeenSubmittedBefore);
  const [activeViolation, setActiveViolation] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(hasBeenSubmittedBefore);
  const [showCompilerAfterSubmit, setShowCompilerAfterSubmit] = useState(false);
  const [idleEvents, setIdleEvents] = useState([]);
  const [codeSnapshots, setCodeSnapshots] = useState([]);
  const [pasteEvents, setPasteEvents] = useState([]);
  const [startedAt, setStartedAt] = useState(null);

  useEffect(() => {
    const initializeSession = async () => {
      await handleEndSession();
      const maxRetries = 3;
      let retries = 0;
      let sessionStarted = false;

      while (retries < maxRetries && !sessionStarted) {
        try {
          await handleStartSession();
          sessionStarted = true;
        } catch (err) {
          // console.error(`Session start attempt ${retries + 1} failed:`, err);
          retries++;
          if (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // console.error("Failed to start session after maximum retries.");
            toast.error("Failed to start session after multiple attempts. Please check the schema file.");
            setError("Unable to initialize session. Please try again later.");
          }
        }
      }
    };

    initializeSession();

    return () => {
      if (sessionRef.current) {
        // console.log("Cleaning up session:", sessionRef.current);
        handleEndSession();
      }
    };
  }, [activeContent?.contentId]);

  useEffect(() => {
    setFiles([
      {
        id: 1,
        name: "main.sql",
        language: "sql",
        content: "-- Write your SQL query here"
      }
    ]);
    setOutput("");
    setError("");
  }, [problemStatement]);

  const activeFile = files[0];
  const activeLanguage = { extension: sql };

  const fetchTables = async () => {
    if (!sessionRef.current) {
      // console.log("No session ID, skipping fetchTables");
      return;
    }
    try {
      const payload = {
        contentId: activeContent?.contentId || "",
        courseId: courseId || "",
        sectionId: sectionId || "",
        userId: cookies.userId || "",
        action: "fetch_tables",
        sessionId: sessionRef.current
      };
      // console.log("Fetching tables with payload:", payload);
      const response = await dbEngine(payload);
      if (response.status === "success") {
        setTables(response);
        // console.log("Tables fetched successfully:", response);
        toast.success("Tables fetched successfully");
      } else {
        // console.error("Failed to fetch tables:", response.message);
        if (response.message?.includes("not active")) {
          // console.log("Session not active, clearing session");
          sessionRef.current = null;
          localStorage.removeItem("db_session_id");
          setTables(null);
          toast.error("Session expired. Please restart the session.");
        }
      }
    } catch (err) {
      // console.error("Error in fetchTables:", err.message);
      toast.error("Failed to fetch tables: " + err.message);
    }
  };

  const handleStartSession = async () => {
    if (sessionRef.current) {
      // console.log("Session already active, reusing session:", sessionRef.current);
      await fetchTables();
      return;
    }
    setLoading(true);
    try {
      const schemaContent = await fetchSchemaContent();
      const payload = {
        contentId: activeContent?.contentId || "",
        courseId: courseId || "",
        sectionId: sectionId || "",
        userId: cookies.userId || "",
        action: "start",
        schema: schemaContent || ""
      };
      // console.log("Starting session with payload:", payload);
      const response = await dbEngine(payload);
      // console.log("Start session response:", response);
      if (response.status === "success" && response.db_name) {
        sessionRef.current = response.db_name;
        localStorage.setItem("db_session_id", response.db_name);
        // console.log("Session started, ID:", response.db_name);
        await fetchTables();
      } else if (response.message === "Session database already active") {
        sessionRef.current = response.db_name || localStorage.getItem("db_session_id") || "";
        if (sessionRef.current) {
          localStorage.setItem("db_session_id", sessionRef.current);
          // console.log("Session already active, using ID:", sessionRef.current);
          await fetchTables();
        } else {
          // console.error("Session active but no ID available.");
          throw new Error("Session active but no ID available.");
        }
      } else {
        // console.error("Session start failed:", response.message);
        throw new Error("Session start failed: " + response.message);
      }
    } catch (err) {
      // console.error("Error in handleStartSession:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchSchemaContent = async () => {
    if (!activeContent?.fileRequired || !activeContent?.fileName) {
      // console.log("No schema file required or fileName missing, returning empty schema");
      return "";
    }

    if (!courseId || !sectionId || !activeContent.fileName) {
      // console.error("Missing required parameters for fetching schema:", { courseId, sectionId, fileName: activeContent.fileName });
      toast.error("Missing course ID, section ID, or file name for schema.");
      throw new Error("Missing required parameters for schema fetch");
    }

    const signedUrlPayload = {
      userId: cookies.userId || "",
      courseId,
      sectionId,
      fileName: activeContent.fileName,
      method: "GET"
    };
    // console.log("Fetching schema with signedUrlPayload:", signedUrlPayload);

    try {
      const signedUrlResponse = await getSignedUrl(signedUrlPayload);
      // console.log("Signed URL response:", signedUrlResponse);

      if (!signedUrlResponse?.data?.preSignedUrl) {
        // console.error("preSignedUrl not found in getSignedUrl response:", signedUrlResponse);
        toast.error("Failed to obtain pre-signed URL for schema file.");
        throw new Error("preSignedUrl not found");
      }

      const fileResp = await fetch(signedUrlResponse.data.preSignedUrl, { method: "GET" });
      if (!fileResp.ok) {
        // console.error(`Failed to fetch schema file: ${fileResp.status} ${fileResp.statusText}`);
        toast.error(`Failed to fetch schema file: ${fileResp.statusText}`);
        throw new Error(`Failed to fetch schema file: ${fileResp.status}`);
      }

      const schemaContent = await fileResp.text();
      // console.log("Schema fetched successfully, content:", schemaContent);
      if (!schemaContent.trim() && activeContent.fileRequired) {
        // console.error("Schema content is empty for required file:", activeContent.fileName);
        toast.error("Schema file is empty.");
        throw new Error("Schema file is empty");
      }
      return schemaContent;
    } catch (err) {
      // console.error("Error in fetchSchemaContent:", err.message);
      toast.error("Error fetching schema: " + err.message);
      throw err;
    }
  };

  const handleRunCode = async () => {
    if (!sessionRef.current) {
      // console.log("No active session. Attempting to start a new session.");
      try {
        await handleStartSession();
      } catch (err) {
        setError("Failed to start a new session.");
        toast.error("Failed to start a new session.");
        return;
      }
      if (!sessionRef.current) {
        setError("Failed to start a new session.");
        toast.error("Failed to start a new session.");
        return;
      }
    }
    const query = activeFile.content.trim();
    if (!query) {
      setError("Query cannot be empty.");
      toast.error("Query cannot be empty.");
      return;
    }
    setLoading(true);
    setOutput("");
    setError("");
    try {
      const payload = {
        contentId: activeContent?.contentId || "",
        courseId: courseId || "",
        sectionId: sectionId || "",
        userId: cookies.userId || "",
        query,
        action: "query",
        sessionId: sessionRef.current
      };
      // console.log("Executing query with payload:", payload);
      const response = await dbEngine(payload);
      // console.log("Run code response:", response);
      if (response.status === "success") {
        setOutput(formatSQLTable(response));
        toast.success("Query executed successfully");
        await fetchTables();
        if (cookies.roleId === ROLES.COURSE_PARTICIPANT && activeContent?.completedStatus !== true) {
          const body = {
            courseId,
            sectionId,
            contentId: activeContent?.contentId,
            completedStatus: true,
            userId: cookies.userId,
            codeSnippet:activeFile.content,
            output: [response.message]

          };
          // console.log("Updating progress with body:", body);
          await updateCodeSandboxProgress(body);
        }
      } else {
        const errorMsg = (response.message || "Query failed.") + (response.details ? ` Details: ${response.details}` : "");
        setError(errorMsg);
        toast.error(errorMsg);
        // console.error("Query execution failed:", response);
        if (response.message?.includes("not active")) {
          // console.log("Session not active, clearing session");
          sessionRef.current = null;
          localStorage.removeItem("db_session_id");
          toast.error("Session expired. Please restart the session.");
        }
      }
    } catch (err) {
      const errorMsg = (err.message || "Error executing query.") + (err.details ? ` Details: ${err.details}` : "");
      setError(errorMsg);
      toast.error(errorMsg);
      // console.error("Error in handleRunCode:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewCode = async () => {
    if (!activeFile.content || activeFile.content.trim() === "") {
      toast.warn("No code available to review!");
      return;
    }
    setReviewLoading(true);
    try {
      const payload = {
        language: activeFile.language || "sql",
        files: [{ name: activeFile.name, content: activeFile.content.trim() }],
        contentId: activeContent?.contentId || "",
        courseId: courseId || "",
        sectionId: sectionId || "",
        userId: cookies.userId || "",
        version: "15"
      };
      // console.log("Code review payload:", payload);
      await handleCodeReview(payload);
      toast.success("Code review PDF downloaded successfully!");
    } catch (err) {
      // console.error("Error in handleReviewCode:", err);
      toast.error(`Failed to generate code review PDF: ${err.message}`);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionRef.current) {
      sessionRef.current = localStorage.getItem("db_session_id") || "";
    }
    if (!sessionRef.current) {
      // console.log("No session to end");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        contentId: activeContent?.contentId || "",
        courseId: courseId || "",
        sectionId: sectionId || "",
        userId: cookies.userId || "",
        action: "end",
        sessionId: sessionRef.current
      };
      // console.log("Ending session with payload:", payload);
      const response = await dbEngine(payload);
      // console.log("End session response:", response);
      if (response.status === "success" || response.status === "warning") {
        sessionRef.current = null;
        localStorage.removeItem("db_session_id");
        setOutput("");
        setError("");
        setTables(null);
        // console.log("Session ended successfully");
      } else {
        // console.error("Session end failed:", response.message);
      }
    } catch (err) {
      // console.error("Error in handleEndSession:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCode = () => {
    setFiles([{ ...files[0], content: "-- Write your SQL query here" }]);
    setOutput("");
    setError("");
    // console.log("Code cleared");
  };

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  const toggleAssistance = () => setIsAssistanceEnabled(prev => !prev);

  // ── Proctoring handlers ──────────────────────────────────────────────────────
  const handleViolation = useCallback((type) => {
    if (type === "copyAttempt") {
      setPasteEvents(prev => [...prev, { type: "pasteEvent", timestamp: new Date().toISOString() }]);
      toast.warn("Paste detected and logged during proctored session.");
    } else {
      setActiveViolation(type);
    }
  }, []);

  const { violationCounts, proctoringLogs, requestFullscreen, isContainerFullscreen } = useProctoring({
    enabled: isProctored && !showProctoringStart,
    onViolation: handleViolation,
    blockCopyPaste: false,
  });

  const totalViolations = Object.values(violationCounts).reduce((sum, n) => sum + n, 0);

  const handleStartProctoredSession = () => {
    requestFullscreen(proctoringContainerRef.current);
    setStartedAt(new Date().toISOString());
    setShowProctoringStart(false);
  };

  const handleResumeAfterViolation = useCallback(() => {
    // Always re-enter fullscreen on resume — the session requires it regardless of
    // which violation (fullscreenExit vs tabSwitch) last set activeViolation.
    if (proctoringContainerRef.current) {
      requestFullscreen(proctoringContainerRef.current);
    }
    setActiveViolation(null);
  }, [requestFullscreen]);

  const handleSubmitCode = useCallback(async (reason = 'timer') => {
    setIsSubmitted(true);
    const currentCode = files[0]?.content || "";
    if (!activeContent?.contentId) {
      toast.error("Cannot submit: content ID is missing.");
      return;
    }
    try {
      const body = {
        courseId,
        sectionId,
        contentId: activeContent.contentId,
        completedStatus: true,
        userId: cookies.userId,
        codeSnippet: currentCode,
        output: reason === 'manual' ? "Manually submitted by student" : "Auto-submitted: timer expired",
        submitReason: reason,
        submittedAt: new Date().toISOString(),
        ...(isProctored && {
          startedAt,
          marksPerTestCase: activeContent?.marksPerTestCase || 1,
          totalTestCases: activeContent?.testCases?.length || 0,
          totalMarks: (activeContent?.marksPerTestCase || 1) * (activeContent?.testCases?.length || 0),
          codeProctoringData: {
            violations: proctoringLogs,
            idleEvents,
            codeSnapshots,
            pasteEvents,
          },
        }),
      };
      await updateCodeSandboxProgress(body);
      if (reason === 'manual') {
        toast.success("✅ Solution submitted successfully!");
      } else {
        toast.warn("⏰ Time's up! Your code has been auto-submitted.");
      }
    } catch (err) {
      // console.error("Submit failed:", err);
      toast.error("Failed to submit. Please try again.");
    }
  }, [cookies, courseId, sectionId, activeContent, files, isProctored, proctoringLogs, idleEvents, codeSnapshots, pasteEvents, startedAt]);

  const timerMinutes = activeContent?.timerMinutes ? Number(activeContent.timerMinutes) : null;
  const [timeLeft, setTimeLeft] = useState(timerMinutes ? timerMinutes * 60 : null);

  const formatTime = (seconds) => {
    if (seconds === null) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!isProctored || showProctoringStart || timeLeft === null || isSubmitted) return;
    if (timeLeft <= 0) { handleSubmitCode(); return; }
    const tick = setInterval(() => setTimeLeft(prev => prev !== null ? prev - 1 : null), 1000);
    return () => clearInterval(tick);
  }, [isProctored, showProctoringStart, timeLeft, isSubmitted]);

  useEffect(() => {
    if (!isProctored || showProctoringStart) return;
    const IDLE_THRESHOLD_MS = 2 * 60 * 1000;
    const resetActivity = () => { lastActivityRef.current = Date.now(); };
    const checkIdle = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle > IDLE_THRESHOLD_MS) {
        setIdleEvents(prev => [...prev, { type: "idleDetected", timestamp: new Date().toISOString(), idleDurationSeconds: Math.round(idle / 1000) }]);
        lastActivityRef.current = Date.now();
      }
    }, 30_000);
    document.addEventListener("keydown", resetActivity, true);
    document.addEventListener("mousemove", resetActivity, { passive: true });
    return () => {
      clearInterval(checkIdle);
      document.removeEventListener("keydown", resetActivity, true);
      document.removeEventListener("mousemove", resetActivity);
    };
  }, [isProctored, showProctoringStart]);

  useEffect(() => {
    if (!isProctored || showProctoringStart) return;
    const snap = setInterval(() => {
      const currentCode = files[0]?.content || "";
      if (currentCode !== lastSnapshotCodeRef.current && currentCode.trim()) {
        setCodeSnapshots(prev => [...prev.slice(-19), { type: "codeSnapshot", timestamp: new Date().toISOString(), charCount: currentCode.length, preview: currentCode.substring(0, 300) }]);
        lastSnapshotCodeRef.current = currentCode;
      }
    }, 3 * 60_000);
    return () => clearInterval(snap);
  }, [isProctored, showProctoringStart, files]);
  // ────────────────────────────────────────────────────────────────────────────

  const renderTables = () => {
    if (!tables || tables.status === "error" || !tables.tables || !Array.isArray(tables.tables)) {
      return (
        <Accordion
          style={{
            backgroundColor: isDarkMode ? "#1a1a2e" : "#ffffff",
            color: isDarkMode ? "#e2e8f0" : "#374151",
            borderRadius: "10px",
            marginTop: "10px",
            boxShadow: "none",
            border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e2e8f0" : "#374151" }} />}
            style={{
              backgroundColor: isDarkMode ? "#2d3748" : "#f8fafc",
              borderRadius: "10px"
            }}
          >
            <Typography style={{ color: isDarkMode ? "#e2e8f0" : "#374151", fontWeight: "600" }}>
              Error or No Tables
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ color: isDarkMode ? "#e2e8f0" : "#374151", width: "100%" }}>
              {tables?.message || "No table data available"}
            </div>
          </AccordionDetails>
        </Accordion>
      );
    }

    return tables.tables.map((table, index) => (
      <Accordion
        key={index}
        style={{
          backgroundColor: isDarkMode ? "#1a1a2e" : "#ffffff",
          color: isDarkMode ? "#e2e8f0" : "#374151",
          borderRadius: "10px",
          marginTop: "10px",
          boxShadow: "none",
          border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e2e8f0" : "#374151" }} />}
          style={{
            backgroundColor: isDarkMode ? "#2d3748" : "#f8fafc",
            borderRadius: "10px"
          }}
        >
          <Typography style={{ color: isDarkMode ? "#e2e8f0" : "#374151", fontWeight: "600" }}>
            Table: {table.name || "Unnamed Table"}
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ padding: "10px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "5px" }}>
            <thead>
              <tr>
                {table.columns?.map((col, colIndex) => (
                  <th
                    key={colIndex}
                    style={{
                      borderBottom: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                      padding: "5px",
                      textAlign: "left",
                      backgroundColor: isDarkMode ? "#2d3748" : "#f8fafc",
                      color: isDarkMode ? "#e2e8f0" : "#374151"
                    }}
                  >
                    {col || "Unnamed Column"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows && Array.isArray(table.rows) ? (
                table.rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    style={{
                      backgroundColor: rowIndex % 2 === 0 ? (isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)") : "transparent"
                    }}
                  >
                    {table.columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          borderBottom: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                          padding: "5px",
                          color: isDarkMode ? "#e2e8f0" : "#374151"
                        }}
                      >
                        {row[col] ?? "NULL"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    style={{
                      borderBottom: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                      padding: "5px",
                      color: isDarkMode ? "#e2e8f0" : "#374151"
                    }}
                  >
                    No rows available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </AccordionDetails>
      </Accordion>
    ));
  };

  const extensions = [
    activeLanguage.extension(),
    ...(isAssistanceEnabled
      ? createAssistanceExtensions(
          problemStatement,
          activeFile.language,
          activeContent?.testCases || [],
          userId,
          setUserId
        )
      : [])
  ];

  return (
    <section
      ref={proctoringContainerRef}
      style={{
        padding: "0",
        backgroundColor: isDarkMode ? "#0f0f23" : "#f8fafc",
        width: isContainerFullscreen ? "100%" : "calc(100% - 390px)",
        marginLeft: isContainerFullscreen ? "0" : "390px",
        marginRight: "0",
        // proctored mode needs a definite height so height:100% inside ProctoredCodeEditorDB works
        minHeight: isProctored
          ? (isContainerFullscreen ? "100vh" : "calc(100vh - 200px)")
          : "auto",
        position: isProctored ? "relative" : "static",
        overflow: isProctored ? "hidden" : "visible",
        boxSizing: "border-box"
      }}
    >
      {isProctored && (
        <ProctoredCodeEditorDB
          isDarkMode={isDarkMode}
          proctoringContainerRef={proctoringContainerRef}
          showProctoringStart={showProctoringStart}
          handleStartProctoredSession={handleStartProctoredSession}
          showSubmitConfirm={showSubmitConfirm}
          setShowSubmitConfirm={setShowSubmitConfirm}
          handleSubmitCode={handleSubmitCode}
          activeViolation={activeViolation}
          totalViolations={totalViolations}
          handleResumeAfterViolation={handleResumeAfterViolation}
          isSubmitted={isSubmitted}
          showCompilerAfterSubmit={showCompilerAfterSubmit}
          setShowCompilerAfterSubmit={setShowCompilerAfterSubmit}
          timeLeft={timeLeft}
          formatTime={formatTime}
          proctoringLogs={proctoringLogs}
          pasteEvents={pasteEvents}
          activeContent={activeContent}
          activeLanguage={{ extension: sql, name: "SQL" }}
          problemStatement={problemStatement}
          activeFile={activeFile}
          loading={loading}
          handleRunCode={handleRunCode}
          handleClearCode={handleClearCode}
          toggleTheme={toggleTheme}
          extensions={extensions}
          output={output}
          error={error}
          tables={tables}
          renderTables={renderTables}
          onChange={(value) => setFiles([{ ...files[0], content: value }])}
        />
      )}
      {!isProctored && (
        <>
      <Container fluid style={{ padding: "0", margin: "0", width: "100%", boxSizing: "border-box" }}>
        {problemStatement && (
          <Row className="mb-3 g-0" style={{ margin: "0", width: "100%" }}>
            <Col style={{ padding: "0" }}>
              <div
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                  color: isDarkMode ? "#e2e8f0" : "#374151",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                <h6 style={{ marginBottom: "10px", color: isDarkMode ? "#7dd3fc" : "#1d4ed8" }}>
                  Problem Statement
                </h6>
                <pre style={{ margin: 0, color: isDarkMode ? "#e2e8f0" : "#374151" }}>
                  {problemStatement}
                </pre>
              </div>
            </Col>
          </Row>
        )}

        <Row className="mb-4 g-0" style={{ margin: "0", width: "100%", display: "flex", flexWrap: "wrap" }}>
          <Col md={6} style={{ padding: "0", flex: "0 0 50%", maxWidth: "50%", boxSizing: "border-box" }}>
            <div
              style={{
                borderRadius: "15px",
                overflow: "hidden",
                padding: "10px",
                background: isDarkMode ? "#1e1e2e" : "#ffffff",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "5px", color: isDarkMode ? "#f7fafc" : "#1e293b" }}>
                Code
              </div>
              <CodeMirror
                value={activeFile.content}
                height="300px"
                extensions={extensions}
                onChange={(value) => setFiles([{ ...files[0], content: value }])}
                theme={isDarkMode ? oneDark : defaultLightThemeOption}
                style={{
                  borderRadius: "10px",
                  backgroundColor: isDarkMode ? "#1a202c" : "#ffffff",
                  color: isDarkMode ? "#e2e8f0" : "#1f2937",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              />
            </div>
          </Col>

          <Col md={6} style={{ padding: "0", flex: "0 0 50%", maxWidth: "50%", boxSizing: "border-box" }}>
            <div
              style={{
                borderRadius: "15px",
                overflow: "hidden",
                padding: "10px",
                background: isDarkMode ? "#1e1e2e" : "#ffffff",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "5px", color: isDarkMode ? "#f7fafc" : "#1e293b" }}>
                Output
              </div>

              <div
                className="d-flex mb-2 gap-2"
                style={{
                  background: isDarkMode ? "linear-gradient(135deg, #2d3748 0%, #1a202c 50%, #2d3748 100%)" : "linear-gradient(135deg, #e0e7ff 0%, #c3daff 50%, #a5b4fc 100%)",
                  borderBottom: isDarkMode ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.08)",
                  padding: "12px 20px"
                }}
              >
                <OverlayTrigger placement="top" overlay={<Tooltip id="run-tooltip">Run Code</Tooltip>}>
                  <button
                    onClick={handleRunCode}
                    disabled={loading}
                    style={{
                      background: isDarkMode ? "#4facfe" : "#3b82f6",
                      color: "white",
                      minWidth: "45px",
                      minHeight: "45px",
                      padding: "8px",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <Play size={20} />}
                  </button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="clear-tooltip">Clear Code</Tooltip>}>
                  <button
                    onClick={handleClearCode}
                    style={{
                      background: isDarkMode ? "#f093fb" : "#f59e0b",
                      color: "white",
                      minWidth: "45px",
                      minHeight: "45px",
                      padding: "8px",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Trash2 size={20} />
                  </button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="review-tooltip">Review Code</Tooltip>}>
                  <Button
                    onClick={handleReviewCode}
                    disabled={reviewLoading || !activeFile.content}
                    style={{
                      background: reviewLoading ? "#6b7280" : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                      color: "white",
                      minWidth: "45px",
                      minHeight: "45px",
                      padding: "8px",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {reviewLoading ? <Spinner size="sm" /> : <FileText size={20} />}
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="theme-tooltip">Toggle Theme</Tooltip>}>
                  <button
                    onClick={toggleTheme}
                    style={{
                      background: isDarkMode ? "#667eea" : "#667eea",
                      color: "white",
                      minWidth: "45px",
                      minHeight: "45px",
                      padding: "8px",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                  </button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip id="assistance-tooltip">Toggle AI Assistance</Tooltip>}>
                  <button
                    onClick={toggleAssistance}
                    disabled={loading}
                    style={{
                      background: isAssistanceEnabled
                        ? (isDarkMode ? "#a78bfa" : "#8b5cf6")
                        : (isDarkMode ? "#d1d5db" : "#9ca3af"),
                      color: isAssistanceEnabled ? "white" : "black",
                      minWidth: "45px",
                      minHeight: "45px",
                      padding: "8px",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {isAssistanceEnabled ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2a10 10 0 0 0-9.6 12.3c-.2 1 .1 2 .8 2.5.6.5 1.5.3 2.2-.4s1.2-1.7 1.8-2.6.9-1.3 1.5-1.9c.7-.6 1.4-.9 2-.9h.2c1.7 0 3.3.6 4.6 1.8 1.4 1.2 2.1 2.9 2.1 4.7 0 1.2-.4 2.3-1 3.4L20 22l-1-1-1-1zm-2 2h4l-2 4L10 4zm-4 4h4L8 12 6 8zm8 0h4l-2 4-2-4zm-4 4h4l-2 4-2-4z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2a10 10 0 0 0-9.6 12.3c-.2 1 .1 2 .8 2.5.6.5 1.5.3 2.2-.4s1.2-1.7 1.8-2.6.9-1.3 1.5-1.9c.7-.6 1.4-.9 2-.9h.2c1.7 0 3.3.6 4.6 1.8 1.4 1.2 2.1 2.9 2.1 4.7 0 1.2-.4 2.3-1 3.4L20 22l-1-1-1-1zm-2 2h4l-2 4L10 4zm-4 4h4L8 12 6 8zm8 0h4l-2 4-2-4zm-4 4h4l-2 4-2-4z" />
                      </svg>
                    )}
                  </button>
                </OverlayTrigger>
              </div>

              <pre
                style={{
                  backgroundColor: isDarkMode ? "#1a202c" : "#ffffff",
                  color: isDarkMode ? "#f8fafc" : "#1f2937",
                  padding: "10px",
                  borderRadius: "10px",
                  overflowX: "auto",
                  minHeight: "50px",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                {error ? error : output || "No output"}
              </pre>
            </div>
          </Col>
        </Row>

        <Row className="g-0" style={{ margin: "0", width: "100%" }}>
          <Col style={{ padding: "0" }}>
            <Accordion
              defaultExpanded
              style={{
                backgroundColor: isDarkMode ? "#0f0f23" : "#f8fafc",
                color: isDarkMode ? "#e2e8f0" : "#374151",
                borderRadius: "10px",
                boxShadow: "none",
                border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e2e8f0" : "#374151" }} />}
                style={{
                  backgroundColor: isDarkMode ? "#2d3748" : "#f8fafc",
                  borderRadius: "10px"
                }}
              >
                <Typography style={{ color: isDarkMode ? "#e2e8f0" : "#374151", fontWeight: "600" }}>
                  Database Tables
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: "10px" }}>
                {renderTables()}
              </AccordionDetails>
            </Accordion>
          </Col>
        </Row>
      </Container>
      <div className="ai-chat-sidebar">
        <ChatComponent
          problemStatement={problemStatement}
          codeSnippet={activeFile.content}
          language={activeFile.language}
          userId={userId}
            output={output}
            testCases={activeContent?.testCases || []} 
        />
      </div>
        </>
      )}
    </section>
  );
};

export default CodeEditorDB;
